import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subscription, catchError, map, of } from 'rxjs';

import { environment } from '../../../../../../../../../environments/environment';
import { TipoOperacaoEnum } from '../../../../../../../../shared/enums/motor-fiscal/tipo-operacao.enum';
import { SelectPesquisavelComponent } from '../../../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { SelectPesquisavelService } from '../../../../../../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import {
    PesquisaCodigoTributacaoModalComponent,
    PesquisaCodigoTributacaoModalData,
    PesquisaCodigoTributacaoModalResult,
} from '../../../../../../../../shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { MaskService } from '../../../../../../../../shared/services/mask.service';
import { AlertService, AlertType } from '../../../../../../../../shared/services/alert.service';
import { MotorFiscalLookupService } from '../../../../../../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';

import { AlertCardComponent } from '../../../../../../../../shared/components/alert-card/alert-card.component';
import { LookupItemStruct } from '../../../../../../../../shared/services/structs/motor-fiscal/lookup-item.struct';
import { LocalizacaoEstoque } from '../../../../../../../../shared/services/models/produto/localizacao-estoque.model';
import { descontoEfetivoDoItem } from '../../../../vd-totais/venda-totais.calculo';
import { ligarPrecoEDesconto } from '../../../venda-item.form';
import { baseDoItem } from '../../../venda-item.calculo';
import { AbntPipe } from '../../../../../../../../shared/pipes/abnt.pipe';

/**
 * Aba "Dados do item" do modal, em subseções como no item da NF-e de saída: Item (acesso rápido,
 * com a localização de estoque), Classificação fiscal e Informações complementares.
 *
 * É a única aba que todo documento tem, e a única onde o preço se negocia. Natureza de operação,
 * CFOP, origem da mercadoria e benefício fiscal ficam aqui, e não nas abas de tributo, porque valem
 * para o item inteiro: são eles que o motor fiscal usa para resolver os tributos. A origem mora no
 * grupo `icms` do item, mas é classificação e não marca o ICMS como informado à mão.
 */
@Component({
    selector: 'app-vd-item-aba-dados',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        NgxMaskDirective,
        AlertCardComponent,
        SelectPesquisavelComponent,
    ],
    templateUrl: './vd-item-aba-dados.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-dados.component.css'],
    // Sem isto o `NgxMaskDirective` estoura NG0201 ao abrir o modal: a diretiva e standalone, mas
    // o provider do ngx-mask nao vem junto com ela.
    providers: [provideNgxMask()],
})
export class VdItemAbaDadosComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public item!: FormGroup;
    @Input() public naturezas: LookupItemStruct[] = [];
    @Input() public origensMercadoria: LookupItemStruct[] = [];

    @Input() public isSomenteLeitura: boolean = false;
    @Input() public exibeDescontoPorItem: boolean = false;

    /** §6.5: acima dele o documento vai exigir liberação ao salvar. Nulo = sem teto configurado. */
    @Input() public tetoDesconto: number | null = null;

    /** Depósitos ativos da empresa: de onde o estoque deste item sai. */
    @Input() public localizacoesEstoque: LocalizacaoEstoque[] = [];

    @Input({ required: true }) public mascaraPreco!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraQuantidade!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraPercentual!: Partial<NgxCurrencyConfig>;

    /** Máscara `0.000`: o CFOP é guardado CRU e formatado só na exibição, como no resto da casa. */
    public readonly masks = MaskService.getMasks();

    /** Naturezas recarregadas pelo botão do campo; antes disso valem as do modal (`naturezas`). */
    private naturezasRecarregadas: LookupItemStruct[] | null = null;
    public isCarregandoNaturezas: boolean = false;

    constructor(
        private dialog: MatDialog,
        private selectPesquisavelService: SelectPesquisavelService,
        private alertService: AlertService,
        private motorFiscalLookupService: MotorFiscalLookupService,
    ) {}

    private assinaturaDoDesconto: Subscription | undefined;

    /** Digitou o desconto, o preço acompanha. O caminho inverso não existe (`ligarPrecoEDesconto`). */
    public ngOnInit(): void {
        this.assinaturaDoDesconto = ligarPrecoEDesconto(this.dados, () => this.mascaraPreco?.precision ?? 2);
    }

    public ngOnDestroy(): void {
        this.assinaturaDoDesconto?.unsubscribe();
    }

    /**
     * Pesquisa de CFOP no MESMO diálogo da tela de natureza de operação.
     *
     * São 318 CFOPs de saída: o protótipo desenha um select com quatro opções, que é atalho de
     * mockup e não sobrevive ao catálogo real. O diálogo pesquisa por código e por descrição, e
     * devolve o código que é gravado SEM pontuação, como manda a convenção da casa.
     */
    public pesquisarCfop(): void {
        const url = `${environment.urlApiMotorFiscal}Lookup/cfop?idTipoOperacao=${TipoOperacaoEnum.Saida}`;

        this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
            width: '700px',
            maxHeight: '85vh',
            panelClass: 'pesquisa-codigo-tributacao-dialog',
            data: {
                titulo: 'Pesquisar CFOP',
                buscarFn: (pesquisa: string) =>
                    this.selectPesquisavelService.getSelectValues<{ codigo?: string; descricao?: string }>(url, pesquisa).pipe(
                        map((list) => ({
                            isError: false,
                            itens: list.map((i) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao ?? '' })),
                        })),
                        catchError(() => of({ isError: true, itens: [] })),
                    ),
                permitirManual: false,
                registrosIniciais: 20,
                valorInicial: this.dados.get('codigoCfop')?.value ?? undefined,
            } as PesquisaCodigoTributacaoModalData,
        })
            .afterClosed()
            .subscribe((resultado: PesquisaCodigoTributacaoModalResult | null) => {
                if (!resultado?.selecionado) return;

                this.dados.get('codigoCfop')?.setValue(resultado.selecionado.codigo.replace(/\D/g, ''));
            });
    }

    public get dados(): FormGroup {
        return this.item.get('dados') as FormGroup;
    }

    public get naturezaControl(): FormControl {
        return this.dados.get('idNatOp') as FormControl;
    }

    public get opcoesDeNatureza(): LookupItemStruct[] {
        return this.naturezasRecarregadas ?? this.naturezas;
    }

    /**
     * Recarrega só as opções da natureza (as de saída, como no cabeçalho); a escolhida não muda. A
     * natureza é cadastrada em outra tela, e quem acabou de criar uma precisa vê-la aqui sem fechar
     * o item.
     */
    public recarregarNaturezas(): void {
        this.isCarregandoNaturezas = true;

        this.motorFiscalLookupService.getNaturezaOperacaoLookup(TipoOperacaoEnum.Saida).subscribe({
            next: (lista) => {
                this.isCarregandoNaturezas = false;
                this.naturezasRecarregadas = lista ?? [];
                this.alertService.show('Naturezas atualizadas', `${this.naturezasRecarregadas.length} disponíveis.`, AlertType.success);
            },
            error: () => {
                this.isCarregandoNaturezas = false;
                this.alertService.show('Erro', 'Não foi possível recarregar as naturezas.', AlertType.error);
            },
        });
    }

    /** Em outra aba, sem perder o item nem o documento: com natureza escolhida abre o cadastro dela; sem, a listagem de naturezas. */
    public abrirNaturezaOperacao(): void {
        const id = this.naturezaControl?.value;

        window.open(id ? `/configuracoes/notas-fiscais/natureza-operacao/cadastrar/${id}` : '/configuracoes/notas-fiscais/natureza-operacao', '_blank');
    }

    public get depositoControl(): FormControl {
        return this.dados.get('idLocalizacaoEstoque') as FormControl;
    }

    public get origemControl(): FormControl {
        return this.item.get('icms.codigoOrigemMercadoria') as FormControl;
    }

    /** Componente de kit: quantidade e descrição vêm do kit e não se negociam na linha (§6.9). */
    public get ehComponenteDeKit(): boolean {
        return this.item.get('kit.numeroKit')?.value != null;
    }

    public get mascaraDaQuantidade(): Partial<NgxCurrencyConfig> {
        return this.dados.get('isUnidadeMedidaFracionada')?.value === false
            ? this.mascaraInteira
            : this.mascaraQuantidade;
    }

    public get total(): number {
        return baseDoItem(this.item);
    }

    /**
     * O aviso só existe quando há teto configurado E o desconto EFETIVO passou dele: a mesma conta
     * da linha e da liberação, que já inclui a redução de preço e a parcela do desconto do documento.
     */
    public get excedeuOTeto(): boolean {
        return this.tetoDesconto != null && descontoEfetivoDoItem(this.item) > this.tetoDesconto;
    }
}
